import React, { useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { makeStyles, tokens } from '@fluentui/react-components';
import { loginRequest } from '../auth/authConfig';

const useStyles = makeStyles({
  page: {
    fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif",
    color: '#1a1a2e',
    backgroundColor: '#fff',
  },
  // Navigation
  nav: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: '20px',
    paddingBottom: '20px',
    paddingLeft: '48px',
    paddingRight: '48px',
    maxWidth: '1200px',
    marginLeft: 'auto',
    marginRight: 'auto',
    '@media (max-width: 767px)': {
      paddingLeft: '20px',
      paddingRight: '20px',
    },
  },
  logo: {
    fontSize: '22px',
    fontWeight: 700,
    color: '#2c5282',
    letterSpacing: '-0.5px',
  },
  navSignIn: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#2c5282',
    cursor: 'pointer',
    border: '1.5px solid #2c5282',
    borderRadius: '8px',
    paddingTop: '8px',
    paddingBottom: '8px',
    paddingLeft: '20px',
    paddingRight: '20px',
    backgroundColor: 'transparent',
    transition: 'all 0.2s ease',
  },
  // Hero
  hero: {
    textAlign: 'center',
    paddingTop: '80px',
    paddingBottom: '40px',
    paddingLeft: '48px',
    paddingRight: '48px',
    maxWidth: '1200px',
    marginLeft: 'auto',
    marginRight: 'auto',
    '@media (max-width: 767px)': {
      paddingTop: '48px',
      paddingBottom: '24px',
      paddingLeft: '20px',
      paddingRight: '20px',
    },
  },
  heroHeadline: {
    fontSize: '56px',
    fontWeight: 800,
    lineHeight: '1.1',
    letterSpacing: '-2px',
    color: '#1a1a2e',
    marginBottom: '20px',
    '@media (max-width: 767px)': {
      fontSize: '36px',
      letterSpacing: '-1px',
    },
  },
  heroSubtitle: {
    fontSize: '20px',
    color: '#6b7280',
    lineHeight: '1.6',
    maxWidth: '600px',
    marginLeft: 'auto',
    marginRight: 'auto',
    marginBottom: '40px',
    '@media (max-width: 767px)': {
      fontSize: '16px',
    },
  },
  heroCta: {
    display: 'inline-block',
    fontSize: '16px',
    fontWeight: 600,
    color: '#fff',
    backgroundColor: '#2c5282',
    border: 'none',
    borderRadius: '10px',
    paddingTop: '14px',
    paddingBottom: '14px',
    paddingLeft: '36px',
    paddingRight: '36px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    boxShadow: '0 4px 14px rgba(44, 82, 130, 0.3)',
  },
  // Screenshot frame
  screenshotWrapper: {
    maxWidth: '1000px',
    marginLeft: 'auto',
    marginRight: 'auto',
    marginTop: '60px',
    marginBottom: '0',
    paddingLeft: '24px',
    paddingRight: '24px',
    '@media (max-width: 767px)': {
      marginTop: '32px',
    },
  },
  screenshotFrame: {
    borderRadius: '12px',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0,0,0,0.05)',
    overflow: 'hidden',
    backgroundColor: '#f8f9fa',
  },
  screenshotBar: {
    height: '36px',
    backgroundColor: '#f0f0f0',
    display: 'flex',
    alignItems: 'center',
    paddingLeft: '14px',
    gap: '8px',
    borderBottom: '1px solid #e5e5e5',
  },
  screenshotDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    backgroundColor: '#ddd',
  },
  screenshotImg: {
    width: '100%',
    display: 'block',
  },
  // Features
  features: {
    paddingTop: '100px',
    paddingBottom: '100px',
    paddingLeft: '48px',
    paddingRight: '48px',
    maxWidth: '1200px',
    marginLeft: 'auto',
    marginRight: 'auto',
    '@media (max-width: 767px)': {
      paddingTop: '60px',
      paddingBottom: '60px',
      paddingLeft: '20px',
      paddingRight: '20px',
    },
  },
  featuresSectionTitle: {
    textAlign: 'center',
    fontSize: '14px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '2px',
    color: '#2c5282',
    marginBottom: '16px',
  },
  featuresSectionHeadline: {
    textAlign: 'center',
    fontSize: '40px',
    fontWeight: 800,
    letterSpacing: '-1px',
    color: '#1a1a2e',
    marginBottom: '64px',
    '@media (max-width: 767px)': {
      fontSize: '28px',
      marginBottom: '40px',
    },
  },
  featuresGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: '32px',
    '@media (max-width: 767px)': {
      gridTemplateColumns: '1fr',
      gap: '40px',
    },
  },
  featureCard: {
    textAlign: 'center',
  },
  featureImgWrapper: {
    borderRadius: '10px',
    overflow: 'hidden',
    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.1)',
    marginBottom: '24px',
    border: '1px solid #eee',
  },
  featureImg: {
    width: '100%',
    display: 'block',
  },
  featureTitle: {
    fontSize: '20px',
    fontWeight: 700,
    color: '#1a1a2e',
    marginBottom: '8px',
  },
  featureDesc: {
    fontSize: '15px',
    color: '#6b7280',
    lineHeight: '1.5',
  },
  // CTA section
  ctaSection: {
    textAlign: 'center',
    paddingTop: '80px',
    paddingBottom: '100px',
    paddingLeft: '48px',
    paddingRight: '48px',
    backgroundColor: '#f8fafc',
    '@media (max-width: 767px)': {
      paddingTop: '60px',
      paddingBottom: '60px',
      paddingLeft: '20px',
      paddingRight: '20px',
    },
  },
  ctaHeadline: {
    fontSize: '40px',
    fontWeight: 800,
    letterSpacing: '-1px',
    color: '#1a1a2e',
    marginBottom: '16px',
    '@media (max-width: 767px)': {
      fontSize: '28px',
    },
  },
  ctaSubtitle: {
    fontSize: '18px',
    color: '#6b7280',
    marginBottom: '40px',
  },
  msButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    height: '52px',
    paddingLeft: '32px',
    paddingRight: '32px',
    backgroundColor: '#fff',
    border: '1.5px solid #e0e0e0',
    borderRadius: '10px',
    cursor: 'pointer',
    fontSize: '15px',
    fontWeight: 600,
    color: '#3b3b3b',
    fontFamily: "'Segoe UI', system-ui, sans-serif",
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    transition: 'all 0.2s ease',
  },
  ctaNote: {
    marginTop: '20px',
    fontSize: '13px',
    color: '#9ca3af',
  },
  // Footer
  footer: {
    textAlign: 'center',
    paddingTop: '24px',
    paddingBottom: '24px',
    fontSize: '13px',
    color: '#9ca3af',
    borderTop: '1px solid #f0f0f0',
  },
});

const MicrosoftLogo: React.FC = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 21 21">
    <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
    <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
    <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
    <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
  </svg>
);

export const LoginPage: React.FC = () => {
  const { instance } = useMsal();
  const styles = useStyles();
  const signInRef = useRef<HTMLDivElement>(null);

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  const handleLogin = () => {
    if (isMobile) {
      instance.loginRedirect(loginRequest).catch(console.error);
    } else {
      instance.loginPopup(loginRequest).catch(console.error);
    }
  };

  const scrollToSignIn = () => {
    signInRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className={styles.page}>
      {/* Nav */}
      <nav className={styles.nav}>
        <div className={styles.logo}>BalanceScript</div>
        <button className={styles.navSignIn} onClick={handleLogin}
          onMouseOver={e => { e.currentTarget.style.backgroundColor = '#2c5282'; e.currentTarget.style.color = '#fff'; }}
          onMouseOut={e => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#2c5282'; }}
        >
          Sign In
        </button>
      </nav>

      {/* Hero */}
      <section className={styles.hero}>
        <h1 className={styles.heroHeadline}>
          Know where every dollar goes.
        </h1>
        <p className={styles.heroSubtitle}>
          The personal check register that tracks your spending, forecasts your balances, and gives you total clarity over your finances.
        </p>
        <button className={styles.heroCta} onClick={scrollToSignIn}
          onMouseOver={e => { e.currentTarget.style.backgroundColor = '#1e3a5f'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
          onMouseOut={e => { e.currentTarget.style.backgroundColor = '#2c5282'; e.currentTarget.style.transform = 'translateY(0)'; }}
        >
          Get Started
        </button>

        {/* Screenshot in browser frame */}
        <div className={styles.screenshotWrapper}>
          <div className={styles.screenshotFrame}>
            <div className={styles.screenshotBar}>
              <div className={styles.screenshotDot} />
              <div className={styles.screenshotDot} />
              <div className={styles.screenshotDot} />
            </div>
            <img
              src="/screenshot-register.png"
              alt="BalanceScript register showing transactions with running balances"
              className={styles.screenshotImg}
              loading="eager"
            />
          </div>
        </div>
      </section>

      {/* Features */}
      <section className={styles.features}>
        <div className={styles.featuresSectionTitle}>Features</div>
        <h2 className={styles.featuresSectionHeadline}>
          Everything you need. Nothing you don't.
        </h2>
        <div className={styles.featuresGrid}>
          <div className={styles.featureCard}>
            <div className={styles.featureImgWrapper}>
              <img
                src="/screenshot-register.png"
                alt="Transaction register view"
                className={styles.featureImg}
                loading="lazy"
              />
            </div>
            <div className={styles.featureTitle}>Track Every Transaction</div>
            <div className={styles.featureDesc}>
              Enter deposits, drafts, and checks with a running balance that updates in real time. Smart payee memory auto-fills amounts you've used before.
            </div>
          </div>
          <div className={styles.featureCard}>
            <div className={styles.featureImgWrapper}>
              <img
                src="/screenshot-insights.png"
                alt="Insights dashboard with spending charts"
                className={styles.featureImg}
                loading="lazy"
              />
            </div>
            <div className={styles.featureTitle}>See the Full Picture</div>
            <div className={styles.featureDesc}>
              Spending breakdowns, recurring costs, and income vs. expenses charts, all generated from your real transaction data.
            </div>
          </div>
          <div className={styles.featureCard}>
            <div className={styles.featureImgWrapper}>
              <img
                src="/screenshot-mobile.png"
                alt="Mobile view of BalanceScript"
                className={styles.featureImg}
                loading="lazy"
              />
            </div>
            <div className={styles.featureTitle}>Works on Any Device</div>
            <div className={styles.featureDesc}>
              Full mobile support with a dedicated touch-friendly interface. Add transactions on the go from your phone or tablet.
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className={styles.ctaSection} ref={signInRef}>
        <h2 className={styles.ctaHeadline}>Ready to take control?</h2>
        <p className={styles.ctaSubtitle}>
          Sign in to start managing your finances with clarity.
        </p>
        <button className={styles.msButton} onClick={handleLogin}
          onMouseOver={e => { e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.1)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
          onMouseOut={e => { e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)'; e.currentTarget.style.transform = 'translateY(0)'; }}
        >
          <MicrosoftLogo />
          Sign in with Microsoft
        </button>
        <div className={styles.ctaNote}>
          Sign in securely with your Microsoft account
        </div>
      </section>

      {/* Footer */}
      <footer className={styles.footer}>
        © {new Date().getFullYear()} BalanceScript
      </footer>
    </div>
  );
};

